import { useCallback, FormEvent, useState, ChangeEvent } from 'react';

const FORM_ID = 'jeannsoftware-contact';
const FORM_URL = 'https://readdy.ai/api/form/d8oh5qrf86hv9evgtp60';

export default function Contact() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [charCount, setCharCount] = useState(0);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (formState === 'submitting') return;
      setFormState('submitting');
      try {
        const body = new URLSearchParams();
        body.append('name', formData.name);
        body.append('email', formData.email);
        body.append('message', formData.message);

        await fetch(FORM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
        setFormState('success');
        setFormData({ name: '', email: '', message: '' });
        setCharCount(0);
      } catch {
        setFormState('error');
      }
    },
    [formState, formData],
  );

  return (
    <footer className="w-full px-6 md:px-12 py-16 md:py-24 bg-foreground-950">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-[40px] bg-foreground-900 p-8 md:p-14">
          <div className="flex flex-col lg:flex-row gap-12 md:gap-16">
            {/* Left - Get in touch */}
            <div className="flex-[2]">
              <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-background-50 leading-[1.12] tracking-tight">
                Get in touch
              </h3>
              <p className="mt-4 font-body text-sm md:text-base text-foreground-400 leading-relaxed max-w-md">
                Have a question or want to discuss a potential collaboration? Reach out
                and we will get back to you as soon as possible.
              </p>

              {/* Contact form */}
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-8 flex flex-col gap-4 max-w-md"
              >
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-foreground-800 text-sm text-background-50 placeholder:text-foreground-500 border border-foreground-700 focus:outline-none focus:border-accent-500/60 transition-colors font-body"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-foreground-800 text-sm text-background-50 placeholder:text-foreground-500 border border-foreground-700 focus:outline-none focus:border-accent-500/60 transition-colors font-body"
                />
                <div className="relative">
                  <textarea
                    name="message"
                    placeholder="Your message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl bg-foreground-800 text-sm text-background-50 placeholder:text-foreground-500 border border-foreground-700 focus:outline-none focus:border-accent-500/60 transition-colors font-body resize-none"
                  />
                  <span className="absolute bottom-3 right-4 font-body text-xs text-foreground-500">
                    {charCount}/500
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="self-start px-6 py-3 rounded-xl bg-accent-500 text-foreground-950 font-label text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {formState === 'submitting' ? 'Sending...' : 'Send message'}
                </button>
                {formState === 'success' && (
                  <p className="font-body text-sm text-accent-400">
                    Message sent successfully. We will be in touch soon.
                  </p>
                )}
                {formState === 'error' && (
                  <p className="font-body text-sm text-red-400">
                    Something went wrong. Please try again.
                  </p>
                )}
              </form>
            </div>

            {/* Middle - Quick links */}
            <div className="flex-1">
              <h4 className="font-label text-xs font-semibold text-background-50 tracking-widest uppercase mb-5">
                Information
              </h4>
              <ul className="flex flex-col gap-3">
                {['Engineering', 'Architecture', 'Performance', 'Quality'].map(
                  (item) => (
                    <li key={item}>
                      <span className="font-body text-sm text-foreground-400 border-b border-foreground-700 pb-0.5">
                        {item}
                      </span>
                    </li>
                  ),
                )}
              </ul>
            </div>

            {/* Right - Contact info */}
            <div className="flex-[1.5]">
              <h4 className="font-label text-xs font-semibold text-background-50 tracking-widest uppercase mb-5">
                Contact
              </h4>
              <div className="flex flex-col gap-1 font-body text-sm text-foreground-400">
                <p className="font-medium text-background-50">+1 2893679675</p>
                <p className="mt-1">contact@jeannsoftware.com</p>
              </div>
              <div className="mt-8">
                <h4 className="font-label text-xs font-semibold text-background-50 tracking-widest uppercase mb-5">
                  Address
                </h4>
                <div className="font-body text-sm text-foreground-400 leading-relaxed">
                  <p>2116 Kedron St</p>
                  <p>Oshawa, L1L 1B9</p>
                  <p>Canada</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-foreground-500">
          <div className="flex items-center gap-5">
            {['Privacy', 'Terms', 'Engineering', 'iOS'].map((item) => (
              <span
                key={item}
                className="font-body text-xs border-b border-foreground-700 pb-0.5"
              >
                {item}
              </span>
            ))}
          </div>
          <p className="font-body text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <span className="font-body text-xs border-b border-foreground-700 pb-0.5">
              Oshawa
            </span>
            <span className="font-body text-xs border-b border-foreground-700 pb-0.5">
              Canada
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}